/**
 * Sidewall Frontend Model Types
 * Types specific to Sidewall tracking UI
 *
 * NOTE: For shared types (EquipmentStatus, HistoryEntry, PartInfo, etc.)
 * import directly from "../../../../shared/models"
 */

import type { EquipmentTreeRowFlat } from "../../../../shared/models";

// ============================================
// Sidewall-Specific Types
// ============================================

/** Burner component types for burner sub-components */
export const BURNER_COMPONENT_TYPE = {
  BLOCK: "block",
  FRONT_FACE: "front_face",
  WATER_JACKET: "water_jacket",
  NOZZLE: "nozzle",
} as const;

export type BurnerComponentType =
  (typeof BURNER_COMPONENT_TYPE)[keyof typeof BURNER_COMPONENT_TYPE];

/** Default burner sub-component positionId (Block) */
export const DEFAULT_BURNER_CHILD_POSITION_ID = 1;

/** Water Jacket sub-component positionId (has its own mesh in the GLB model) */
export const WATER_JACKET_POSITION_ID = 3;

/**
 * Sidewall-specific tree row with additional fields for burner tracking
 * Note: HasChildren is inherited from EquipmentTreeRowFlat and indicates
 * positions with sub-components (burner panels 3, 5, 9)
 */
export interface SidewallTreeRow extends EquipmentTreeRowFlat {
  /** Parent burner panel position (for burner sub-components: 3, 5, or 9) */
  ParentBurnerPositionId?: number;
  /** BurnerPanel PartId (601, 602, 603) - used as ParentPartId for MovePart API */
  BurnerPanelPartId?: number;
  /** For burner sub-components: which burner component this is */
  BurnerComponentType?: BurnerComponentType;
  /** For slot containers (Upper Location, Lower Location): whether a shell is installed */
  HasShellInstalled?: boolean;
  /** For slot containers: the PartTypeId of the slot (312=Upper, 313=Lower) */
  SlotPartTypeId?: number;
}

/**
 * Type guard for sidewall rows with burner sub-components (positions 3, 5, 9)
 */
export const hasBurnerSubComponents = (row: SidewallTreeRow): boolean => {
  return row.HasChildren === true;
};

/**
 * Type guard for burner sub-component rows (Block, Front Face, Water Jacket, Nozzle)
 */
export const isBurnerSubComponent = (row: SidewallTreeRow): boolean => {
  return row.ParentBurnerPositionId !== undefined;
};
